import { goToUrl } from "apps/visits/core/utils/go-to-url";
import { t } from "i18next";
import { useEffect } from "react";
import type { ItemListId } from "../../tracking/create-ecommerce-event";
import { useConfirmOrder } from "../api/confirm-order.mutation";
import { useGetOrder } from "../api/get-order.query";
import { useCartStore } from "../store/use-cart-store";
import { addPaymentInfoEvent, safeTrackEvent } from "../tracking/events";
import { readGaUserType } from "../utils/ga-user-type";
import { writeGaoItemListIds } from "../utils/gao-item-list-ids";
import { useRoutes } from "./use-routes";

export const useConfirmOrderHandler = (uuid: string) => {
  const { mutate: confirmOrder, error: confirmOrderError, isLoading } = useConfirmOrder({ uuid });
  const { data: orderData } = useGetOrder({ uuid, config: { enabled: !!uuid } });
  const { goToThankYouPage, goToHomePage } = useRoutes();
  const confirmOrderErrorFormatted = confirmOrderError
    ? t("GROUP_ACTIONS_ORDERING.ERRORS.ORDER_CANNOT_BE_CONFIRMED")
    : null;

  useEffect(() => {
    if (confirmOrderError) {
      setTimeout(() => {
        goToHomePage();
      }, 10000);
    }
  }, [confirmOrderError, goToHomePage]);

  const handleConfirm = () => {
    confirmOrder(uuid, {
      onSuccess: (response) => {
        // REV-115: payment intent. Items + value taken from getOrder snapshot — confirmOrder
        // response strips items so we read the cached order data populated by useGetOrder.
        const itemsSource = orderData?.order ?? response.order;
        const cartProducts = useCartStore.getState().products;
        const itemListIdsByUuid = cartProducts.reduce<Record<string, ItemListId>>((acc, p) => {
          if (p.item_list_id) acc[p.uuid] = p.item_list_id;
          return acc;
        }, {});
        const items = (itemsSource.items ?? []).map((item) => ({
          uuid: item.productUuid,
          name: item.name,
          grossPrice: String(item.grossPrice),
          productType: "blood-test-package" as const,
          quantity: 1,
          item_list_id: itemListIdsByUuid[item.productUuid] ?? ("listing" as ItemListId),
        }));

        // REV-115 / REV-15: emit add_payment_info on every successful confirmation — the checkout
        // step is completed regardless of basket value. Free (0 zł) orders report paymentType
        // "free" (they bypass PayU), paid orders report "PayU". Mirrors the voucher-distribution
        // convention (REV-164) and keeps the funnel symmetric with begin_checkout / purchase.
        const value = Number(response.order.totalGrossPrice);
        const isFree = value <= 0;
        safeTrackEvent(
          () =>
            addPaymentInfoEvent(
              items,
              { value, transactionId: response.order.uuid },
              { paymentType: isFree ? "free" : "PayU", numberOfProducts: items.length, userType: readGaUserType() },
            ),
          "add_payment_info",
          "confirm-order onSuccess",
        );

        // Persist item_list_id per product uuid so the TYP purchase event can attribute source
        // after the cart has been reset on thank-you mount. Fallback "listing" applied here too
        // so every item in the order has an attributable source.
        const itemListIdsForStorage = (itemsSource.items ?? []).reduce<Record<string, ItemListId>>((acc, item) => {
          acc[item.productUuid] = itemListIdsByUuid[item.productUuid] ?? ("listing" as ItemListId);
          return acc;
        }, {});
        writeGaoItemListIds(itemListIdsForStorage);

        if (response.order.paymentLink && response.order.status === "in_realization") {
          goToUrl(response.order.paymentLink);
        } else if (response.order.status === "completed") {
          goToThankYouPage(response.order.uuid);
        }
      },
    });
  };

  return { handleConfirm, isLoading, confirmOrderError: confirmOrderErrorFormatted };
};
